/**
 * When a session's operational detail disappears.
 *
 * Runs, scores and notes are kept for the institution's retention window — a
 * year by default — and then deleted. Statistics survive, and contain nobody's
 * name.
 *
 * The date is shown **before** it happens. A teacher who loses last term's
 * notes without warning has been surprised by their own tool, and the warning
 * is worth more than the policy page it would otherwise be buried in.
 */

/** How close is close enough to warn about. */
export const WARN_WITHIN_DAYS = 30;

export function deletionDate(startedAt: string, operationalDays: number): Date | undefined {
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return undefined;
  return new Date(started + operationalDays * 86_400_000);
}

/**
 * Whole days from now until deletion.
 *
 * Counted in calendar days from midnight UTC, not in elapsed milliseconds: a
 * session deleted "tomorrow" should say tomorrow all day, rather than becoming
 * "today" at some point in the afternoon.
 */
export function daysUntilDeletion(
  startedAt: string,
  operationalDays: number,
  now: number
): number | undefined {
  const deletes = deletionDate(startedAt, operationalDays);
  if (!deletes) return undefined;

  const startOfDay = (ms: number) => {
    const date = new Date(ms);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  };
  return Math.round((startOfDay(deletes.getTime()) - startOfDay(now)) / 86_400_000);
}

export type RetentionState = "distant" | "soon" | "gone";

export function retentionState(
  startedAt: string,
  operationalDays: number,
  now: number
): RetentionState {
  const days = daysUntilDeletion(startedAt, operationalDays, now);
  if (days === undefined) return "distant";
  if (days <= 0) return "gone";
  return days <= WARN_WITHIN_DAYS ? "soon" : "distant";
}
